import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dotwApi, resolveImageUrl } from '../lib/api';
import { getCrownVicImage } from '../lib/images';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trophy, MapPin, Calendar, ChevronLeft, ChevronRight, Settings, Flag, ArrowLeft, ChevronDown } from 'lucide-react';
import SubmitDriverDialog from '../components/SubmitDriverDialog';

function DriverCard({ driver }) {
  const mainIdx = driver.main_image || 0;
  const imgUrl = driver.images?.[mainIdx] || driver.images?.[0];
  return (
    <Link to={`/driver-of-the-week/${driver.id}`} data-testid={`dotw-card-${driver.id}`}>
      <Card className="bg-[#141414] border-[#262626] overflow-hidden card-hover h-full flex flex-col">
        <div className="relative h-56 bg-[#1F1F1F]">
          {imgUrl ? (
            <img src={resolveImageUrl(imgUrl)} alt={driver.driver_name} className="w-full h-full object-cover" onError={(e) => { e.target.src = getCrownVicImage(driver.id); }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Trophy className="w-12 h-12 text-gray-700" /></div>
          )}
          {driver.car_number && (
            <span className="absolute top-3 left-3 bg-[#FF3B30] text-white font-bold text-sm px-3 py-1 rounded">#{driver.car_number}</span>
          )}
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <h3 className="font-heading font-bold text-xl text-white mb-2">{driver.driver_name}</h3>
          {driver.hometown && (
            <p className="text-gray-400 text-sm flex items-center gap-1.5 mb-4">
              <MapPin className="w-4 h-4 text-[#FF3B30]" /> {driver.hometown}
            </p>
          )}
          <div className="mt-auto">
            <span className="inline-flex items-center text-[#FF3B30] text-sm font-medium hover:underline">
              View Profile <ChevronLeft className="w-4 h-4 ml-1 rotate-180" />
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function DriverOfTheWeekPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(null);
  const [currentDrivers, setCurrentDrivers] = useState([]);
  const [pastWeeks, setPastWeeks] = useState([]);
  const [singleDriver, setSingleDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pastSkip, setPastSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentImg, setCurrentImg] = useState(0);

  const isAdmin = user && user.role === 'admin';

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const res = await dotwApi.getOne(id);
          setSingleDriver(res.data);
        } else {
          const [weekRes, weeksRes] = await Promise.all([
            dotwApi.getCurrentWeek(),
            dotwApi.getWeeks(0, 10),
          ]);
          setCurrentWeek(weekRes.data.week);
          setCurrentDrivers(weekRes.data.drivers || []);
          // Filter out current week from past weeks
          const past = (weeksRes.data || []).filter(w => w.week !== weekRes.data.week);
          setPastWeeks(past);
          setPastSkip(10);
          setHasMore(weeksRes.data.length >= 10);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id]);

  const loadMorePast = async () => {
    setLoadingMore(true);
    try {
      const res = await dotwApi.getWeeks(pastSkip, 10);
      const newWeeks = (res.data || []).filter(w => w.week !== currentWeek);
      setPastWeeks(prev => [...prev, ...newWeeks]);
      setPastSkip(prev => prev + 10);
      setHasMore(res.data.length >= 10);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  const formatWeek = (w) => {
    if (!w) return '';
    const d = new Date(w + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  // ========== SINGLE DRIVER PROFILE ==========
  if (singleDriver) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <div className="bg-[#141414] border-b border-[#262626]">
          <div className="container mx-auto px-4 py-6">
            <Link to="/driver-of-the-week" className="inline-flex items-center text-gray-400 hover:text-white mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Drivers
            </Link>
          </div>
        </div>
        <div className="container mx-auto px-4 py-10">
          {/* Driver Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-lg bg-[#FF3B30] flex items-center justify-center flex-shrink-0">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">{singleDriver.driver_name}</h1>
              <div className="flex items-center gap-4 text-gray-400 mt-1">
                {singleDriver.hometown && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF3B30]" />{singleDriver.hometown}</span>}
                {singleDriver.car_number && <span className="flex items-center gap-1.5"><Flag className="w-4 h-4 text-[#FF3B30]" />Car #{singleDriver.car_number}</span>}
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gray-500" />Week of {formatWeek(singleDriver.week_start)}</span>
              </div>
            </div>
          </div>

          {/* Image Gallery */}
          {singleDriver.images?.length > 0 && (
            <div className="mb-10">
              <div className="relative aspect-[16/9] max-h-[500px] bg-[#141414] rounded-lg overflow-hidden border border-[#262626] mb-3">
                <img src={resolveImageUrl(singleDriver.images[currentImg])} alt={singleDriver.driver_name} className="w-full h-full object-contain" onError={(e) => { e.target.src = getCrownVicImage(singleDriver.id); }} />
                {singleDriver.images.length > 1 && (
                  <>
                    <button onClick={() => setCurrentImg(p => p === 0 ? singleDriver.images.length - 1 : p - 1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white"><ChevronLeft className="w-6 h-6" /></button>
                    <button onClick={() => setCurrentImg(p => (p + 1) % singleDriver.images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center text-white"><ChevronRight className="w-6 h-6" /></button>
                  </>
                )}
              </div>
              {singleDriver.images.length > 1 && (
                <div className="flex gap-2">
                  {singleDriver.images.map((img, i) => (
                    <button key={i} onClick={() => setCurrentImg(i)} className={`w-24 h-16 rounded overflow-hidden border-2 flex-shrink-0 ${i === currentImg ? 'border-[#FF3B30]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                      <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Q&A */}
          <div className="max-w-3xl">
            <h3 className="font-heading font-bold text-2xl text-white mb-6 flex items-center gap-3">
              <div className="w-1 h-8 bg-[#FF3B30] rounded"></div>
              Get to Know {singleDriver.driver_name?.split(' ')[0] || 'the Driver'}
            </h3>
            <div className="space-y-6">
              {(singleDriver.qa || []).filter(q => q.answer).map((item, i) => (
                <div key={i} className="border-b border-[#262626] pb-6 last:border-0">
                  <p className="text-[#FF3B30] font-medium text-sm uppercase tracking-wider mb-2">{item.question}</p>
                  <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== MAIN LISTING PAGE ==========
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-start justify-between">
            <div className="max-w-2xl">
              <p className="tag text-[#FF3B30] mb-3">Weekly Spotlight</p>
              <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">Drivers of the Week</h1>
              <p className="text-gray-400">
                Each week we spotlight 3 Crown Vic racers from short tracks across the United States.
                Learn their stories, see their rides, and find out what drives them to race.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <SubmitDriverDialog />
              {isAdmin && (
                <Link to="/admin/driver-of-the-week">
                  <Button variant="outline" className="border-[#262626] text-white" data-testid="manage-dotw-btn">
                    <Settings className="w-4 h-4 mr-2" /> Manage
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        {/* This Week's Drivers */}
        {currentDrivers.length > 0 ? (
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1.5 h-8 bg-[#FF3B30] rounded"></div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white">This Week's Featured Drivers</h2>
                <p className="text-gray-500 text-sm">Week of {formatWeek(currentWeek)}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentDrivers.map((driver) => (
                <DriverCard key={driver.id} driver={driver} />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16 mb-16">
            <Trophy className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white mb-4">No Drivers Featured This Week</h2>
            <p className="text-gray-400">Check back soon for this week's featured drivers!</p>
          </div>
        )}

        {/* Past Drivers Archive */}
        {pastWeeks.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1.5 h-8 bg-[#262626] rounded"></div>
              <h2 className="font-heading font-bold text-2xl text-white">Past Featured Drivers</h2>
            </div>

            <div className="space-y-12">
              {pastWeeks.map((week) => (
                <div key={week.week}>
                  <p className="text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Week of {formatWeek(week.week)}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {week.drivers.map((driver) => (
                      <DriverCard key={driver.id} driver={driver} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-10">
                <Button
                  onClick={loadMorePast}
                  disabled={loadingMore}
                  variant="outline"
                  className="border-[#262626] text-white px-8"
                  data-testid="load-more-past-btn"
                >
                  <ChevronDown className="w-4 h-4 mr-2" />
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
